import {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
  ReactElement,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
// @ts-ignore - auth0-js is not typed
import { default as auth0 } from "auth0-js";
import { getProtocolHost, getAuthReturnPath, setAuthReturnPath } from "helpers";
import { clearRemotePatientSession } from "helpers/clear-local-stoage";
import { jwtDecode, JwtPayload } from "jwt-decode";
import * as Sentry from "@sentry/react";
import { Span } from "@sentry/tracing";
import {
  AUTH0_DOMAIN,
  AUTH0_PATIENTS_CLIENT_ID,
  AUTH0_PATIENTS_CONNECTION,
  SPECIALIST_HOSTNAME,
} from "env-vars";
import { requestPasswordReset } from "helpers/request-password-reset";
import { SurveyKind } from "../survey";

const webAuth = new auth0.WebAuth({
  domain: AUTH0_DOMAIN,
  clientID: AUTH0_PATIENTS_CLIENT_ID,
  redirectUri: getProtocolHost(),
  responseType: "token id_token",
  scope: "openid profile email",
});

interface AppMetadata {
  ciq_id?: string;
  location_id?: string;
}

interface CustomJwtPayload extends JwtPayload {
  email?: string;
  app_metadata?: AppMetadata;
}

enum EmailLinkError {
  GENERAL_ERROR = "general",
  UNAUTHORIZED = "unauthorized",
  NO_LOCATION_ID = "no_location_id",
  INCORRECT_LOCATION_ID = "incorrect_location_id",
}

enum EmailPasswordError {
  ACCESS_DENIED = "access_denied",
  GENERAL_ERROR = "general",
}

// eslint-disable-next-line react-refresh/only-export-components
export const emailLinkErrorMessage: Record<EmailLinkError, string> = {
  [EmailLinkError.GENERAL_ERROR]:
    "There was an error signing you in. Please try again.",
  [EmailLinkError.UNAUTHORIZED]:
    "The link has expired after 15 minutes or has already been used. Please enter your email to receive a new link.",
  [EmailLinkError.NO_LOCATION_ID]:
    "Your account is missing a sign-up location. Please use the link you received to create your account, or reach out to your health system for support.",
  [EmailLinkError.INCORRECT_LOCATION_ID]:
    "Incorrect account sign-up location. Please reach out to your health system for support.",
};

// eslint-disable-next-line react-refresh/only-export-components
export const emailPasswordErrorMsg: Record<EmailPasswordError, string> = {
  [EmailPasswordError.ACCESS_DENIED]:
    "Invalid email or password. Please try again.",
  [EmailPasswordError.GENERAL_ERROR]:
    "Error submitting credentials. Please try again.",
};

export type PatientAuthContextType = {
  isLoading: boolean;
  isEmailSent: boolean;
  isEmailResent: boolean;
  isSubmitting: boolean;
  emailAddress: string;
  isAuthenticated: boolean;
  passwordlessError: boolean;
  emailLinkError: EmailLinkError | undefined;
  emailPasswordError: EmailPasswordError | undefined;
  forgotPasswordError: boolean;
  resendCodeRemainingSeconds: number;
  isResendCodeTimerActive: boolean;
  logout: () => void;
  clearPasswordlessState: () => void;
  setSubmitting: (value: boolean) => void;
  sendEmailWithCode: (email: string, resend?: boolean) => void;
  setPasswordlessError: (value: boolean) => void;
  setEmailLinkError: (value: EmailLinkError | undefined) => void;
  setEmailPasswordError: (value: EmailPasswordError | undefined) => void;
  setForgotPasswordError: (value: boolean) => void;
  submitCredentials: (email: string, password: string) => void;
  requestForgotPasswordReset: (email: string) => void;
  verifyLoginCode: (code: string) => void;
  resendCodeCount: number;
  setResendCodeCount: React.Dispatch<React.SetStateAction<number>>;
  refreshPasswordlessToken: () => Promise<boolean>;
  markMagicLinkAuthenticated: () => void;
};

export const PatientAuthContext = createContext<
  PatientAuthContextType | undefined
>(undefined);

const logError = (message: string, err: unknown) => {
  Sentry.captureMessage(message, { extra: { err } });
};

type AuthResult = {
  idToken: string;
  idTokenPayload: {
    app_metadata: {
      location_id: string;
      ciq_id: string;
    };
    email: string;
  };
  appState: string;
};

type Props = {
  children: ReactElement;
};

// for params that need to be preserved through auth0 login redirect
type LaunchState = {
  surveyKind?: SurveyKind;
  qs_location_id: string;
};

function getLaunchState(): LaunchState {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    // surveyKind overrides the default initial/rescreen survey logic
    surveyKind: urlParams.get("kind") as SurveyKind,
    // qs_location_id is only used for new users
    qs_location_id: urlParams.get("location_id") as string,
  };
}

function encodeLaunchState(): string {
  const state = getLaunchState();
  return btoa(JSON.stringify(state));
}

function decodeLaunchState(state: string): LaunchState | undefined {
  return state ? (JSON.parse(atob(state)) as LaunchState) : undefined;
}

const PASSWORDLESS_EMAIL_STORAGE_KEY = "passwordlessEmail";
const RESEND_TIMER_STORAGE_KEY = "resendCodeTimer";
const COOLDOWN_SECONDS = 60; // 1 minutes

const calculateRemainingTime = (): number => {
  const stored = localStorage.getItem(RESEND_TIMER_STORAGE_KEY);
  if (!stored) return 0;

  const expiresAt = Number(stored);
  const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));

  if (remaining === 0) {
    localStorage.removeItem(RESEND_TIMER_STORAGE_KEY);
  }

  return remaining;
};

const startResendTimer = (): void => {
  const expiresAt = Date.now() + COOLDOWN_SECONDS * 1000;
  localStorage.setItem(RESEND_TIMER_STORAGE_KEY, String(expiresAt));
};

const clearTimerState = (): void => {
  localStorage.removeItem(PASSWORDLESS_EMAIL_STORAGE_KEY);
  localStorage.removeItem(RESEND_TIMER_STORAGE_KEY);
};

export function PatientAuthProvider({ children }: Props) {
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setLoading] = useState(true);
  const [isEmailSent, setEmailSent] = useState(false);
  const [isEmailResent, setEmailResent] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [emailAddress, setEmailAddress] = useState(() => {
    return localStorage.getItem(PASSWORDLESS_EMAIL_STORAGE_KEY) || "";
  });
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [passwordlessError, setPasswordlessError] = useState(false);
  const [emailPasswordError, setEmailPasswordError] = useState<
    EmailPasswordError | undefined
  >(undefined);
  const [emailLinkError, setEmailLinkError] = useState<
    EmailLinkError | undefined
  >(undefined);
  const [forgotPasswordError, setForgotPasswordError] = useState(false);
  const [resendCodeCount, setResendCodeCount] = useState(0);

  // Timer state
  const [resendCodeRemainingSeconds, setResendCodeRemainingSeconds] = useState(
    () => calculateRemainingTime()
  );

  const isResendCodeTimerActive = resendCodeRemainingSeconds > 0;

  // Timer countdown effect
  useEffect(() => {
    if (!isResendCodeTimerActive) return;

    const intervalId = setInterval(() => {
      const remaining = calculateRemainingTime();
      setResendCodeRemainingSeconds(remaining);

      if (remaining === 0) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isResendCodeTimerActive]);

  const handleTokenAndRedirect = useCallback(
    (hadLaunchLocation: boolean) => {
      const idTokenExp = localStorage.getItem("id_token_exp");
      const idToken = localStorage.getItem("id_token");

      const isTokenValid =
        Boolean(idToken) &&
        Boolean(idTokenExp) &&
        Number(idTokenExp) > Date.now();

      setAuthenticated(isTokenValid);

      if (!isTokenValid) {
        console.info("token is not valid");

        // When user has magic-link token (pa), PatientRoute guard redirects to /login/verify.
        // We still set the deep-link return path here so after verification they can be sent
        // back to the original path (e.g. /complete). The effect may run with the pre-redirect
        // location before the guard’s redirect is applied.
        const urlParams = new URLSearchParams(location.search);
        const paToken = urlParams.get("pa");
        if (paToken) {
          const returnUrlParams = new URLSearchParams(location.search);
          returnUrlParams.delete("pa");
          const returnSearch = returnUrlParams.toString();
          const returnPath =
            location.pathname + (returnSearch ? `?${returnSearch}` : "");
          setAuthReturnPath(returnPath);
          setLoading(false);
          return;
        }

        clearRemotePatientSession();
        setLoading(false);

        return;
      }

      // Extract survey kind from URL for authenticated users clicking email links
      const { surveyKind } = getLaunchState();
      if (surveyKind) {
        localStorage.setItem("launch_survey_kind", surveyKind);
      }

      const locationId = localStorage.getItem("location_id");
      const ciqId = localStorage.getItem("ciq_id");

      if (!locationId) {
        console.info("locationId is missing");
        setEmailLinkError(
          hadLaunchLocation
            ? EmailLinkError.INCORRECT_LOCATION_ID
            : EmailLinkError.NO_LOCATION_ID
        );
        clearRemotePatientSession();
        setAuthenticated(false);
        setLoading(false);

        history.push("/login/passwordless");

        return;
      }

      if (!ciqId) {
        console.info("ciqId is missing");
        history.push("/login/info");
        setLoading(false);
        return;
      }

      const returnPath = getAuthReturnPath();
      if (returnPath) {
        history.replace(returnPath);
      }

      setLoading(false);
    },
    [history, location]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    // Skip Auth0 parsing for all login pages to prevent infinite loops
    // when user is redirected without authentication
    if (location.pathname.startsWith("/login/")) {
      setLoading(false);
      return;
    }

    const accessTokenParam = urlParams.get("at");

    if (accessTokenParam) {
      try {
        const decoded = jwtDecode<CustomJwtPayload>(accessTokenParam);
        const { exp = 0 } = decoded;
        const calculatedExpiry = (exp * 1000).toString();

        if (Date.now() < Number(calculatedExpiry)) {
          localStorage.setItem("id_token", accessTokenParam);
          localStorage.setItem("id_token_exp", calculatedExpiry);

          const appMetadata = decoded.app_metadata;
          if (appMetadata?.location_id) {
            localStorage.setItem("location_id", appMetadata.location_id);
          }
          if (appMetadata?.ciq_id) {
            localStorage.setItem("ciq_id", appMetadata.ciq_id);
          }
          if (decoded.email) {
            localStorage.setItem("ciq_email", decoded.email);
          }

          // Clear EHR session state on fresh login
          sessionStorage.removeItem("ehr_connected");
          sessionStorage.removeItem("ehr_skipped");
          sessionStorage.removeItem("ehr_flow_complete");

          setAuthenticated(true);
          setLoading(false);

          // Remove 'at' param but keep others if any, or just redirect to complete
          // The plan said "redirect to the original complete page"
          // Assuming we are already on the page we want to be, or we want to go to /complete
          // If we are at /complete?at=..., we just want /complete

          // Let's preserve other params but remove 'at'
          urlParams.delete("at");
          const newSearch = urlParams.toString();

          history.replace({
            pathname: location.pathname,
            search: newSearch,
          });

          return;
        }
      } catch (e) {
        console.error("Failed to process access token param", e);
      }
    }

    webAuth.parseHash((error: unknown, authResult: AuthResult) => {
      // This code runs on page load. Only record a transaction if there is a login result.
      let transaction: Span | undefined = undefined;
      if (error || authResult) {
        transaction = Sentry.startTransaction({ name: "Process AuthResult" });
        Sentry.getCurrentHub().configureScope((scope) =>
          scope.setSpan(transaction)
        );
      }

      if (error) {
        // @ts-ignore no types
        const errorType = error.error;
        // @ts-ignore no types
        const errorDesc = error.errorDescription;

        if (
          errorType === EmailLinkError.UNAUTHORIZED &&
          errorDesc.includes("Wrong email or verification code")
        ) {
          setEmailLinkError(EmailLinkError.UNAUTHORIZED);
        } else {
          setEmailLinkError(EmailLinkError.GENERAL_ERROR);
        }

        console.error(
          `Error from parseHash. error: "${errorType}", desc: "${errorDesc}"`
        );
        clearRemotePatientSession();
        clearTimerState();
        setAuthenticated(false);
        setLoading(false);

        transaction?.setStatus("unauthenticated");
        transaction?.finish();

        return;
      }

      const idToken = authResult?.idToken;
      const idTokenPayload = authResult?.idTokenPayload;
      let hadLaunchLocation = false;

      if (idToken && idTokenPayload) {
        console.info("reading result");
        localStorage.setItem("id_token", authResult.idToken);

        const { exp = 0 } = jwtDecode<JwtPayload>(idToken);

        const calculatedExpiry = (exp * 1000).toString();

        localStorage.setItem("id_token_exp", calculatedExpiry);

        const appMetaData = authResult?.idTokenPayload?.app_metadata;
        const email = authResult?.idTokenPayload.email;
        const locationId = appMetaData?.location_id;
        const ciqId = appMetaData?.ciq_id;

        if (locationId) {
          localStorage.setItem("location_id", locationId);
        }
        if (ciqId) {
          localStorage.setItem("ciq_id", ciqId);
        }
        if (email) {
          localStorage.setItem("ciq_email", email);
        }

        const launchState = decodeLaunchState(authResult?.appState);
        const launchSurveyKind = launchState?.surveyKind;
        hadLaunchLocation = Boolean(launchState?.qs_location_id);

        if (launchSurveyKind) {
          localStorage.setItem("launch_survey_kind", String(launchSurveyKind));
        }

        localStorage.setItem("ciq_can_show_language_prompt", "true");

        // Clear EHR session state on fresh login
        sessionStorage.removeItem("ehr_connected");
        sessionStorage.removeItem("ehr_skipped");
        sessionStorage.removeItem("ehr_flow_complete");

        // Clears hash in URL produced by Auth0 redirect
        if (window.location.hash) {
          history.replace({
            pathname: location.pathname,
            search: location.search,
          });
        }

        console.info("finished processing result");
        transaction?.setStatus("ok");
        transaction?.finish();
      }

      handleTokenAndRedirect(hadLaunchLocation);
    });
  }, [history, location.search, location.pathname, handleTokenAndRedirect]);

  const sendEmailWithCode = useCallback(
    (email: string, resend = false) => {
      setEmailSent(false);
      setEmailResent(false);

      webAuth.passwordlessStart(
        {
          connection: "email",
          send: "code",
          email: email,
          authParams: {
            // state is used by auth0 actions for new user sign-up
            state: encodeLaunchState(),
            // appState is used by this client
            appState: encodeLaunchState(),
          },
        },
        function (err: any) {
          setPasswordlessError(Boolean(err));
          setEmailSent(true);
          setSubmitting(false);

          if (resend) {
            setEmailResent(true);
          }

          if (err) {
            logError("Passwordless start error", err);

            if (err.code === "access_denied") {
              setEmailLinkError(EmailLinkError.UNAUTHORIZED);
            } else {
              setEmailLinkError(EmailLinkError.GENERAL_ERROR);
            }
          } else {
            setEmailAddress(email);
            localStorage.setItem(PASSWORDLESS_EMAIL_STORAGE_KEY, email);
            startResendTimer();
            setResendCodeRemainingSeconds(COOLDOWN_SECONDS); // Immediately update state

            history.push({
              pathname: "/login/passwordless/confirm",
              search: location.search,
            });
          }
        }
      );
    },
    [location, history]
  );

  const verifyLoginCode = useCallback(
    (code: string) => {
      webAuth.passwordlessLogin(
        {
          connection: "email",
          email: emailAddress,
          verificationCode: code,
          // state is used by auth0 actions for new user sign-up
          state: encodeLaunchState(),
          // appState is used by this client
          appState: encodeLaunchState(),
        },
        function (err: Error, res: unknown) {
          if (err) {
            console.error("Code verification failed", err);
            setEmailLinkError(EmailLinkError.UNAUTHORIZED);
          }
          if (res) {
            const returnPath = getAuthReturnPath();
            window.location.assign(returnPath || "/");
          }
        }
      );
    },
    [emailAddress]
  );

  const submitCredentials = useCallback((email: string, password: string) => {
    webAuth.login(
      {
        realm: AUTH0_PATIENTS_CONNECTION,
        email: email,
        password: password,
        // state is not included here since there is no new user sign-up flow w/password login
        // appState is used by this client
        appState: encodeLaunchState(),
      },
      function (err: Error) {
        if (err) {
          setSubmitting(false);
          // handle login error result
          // @ts-ignore no error types
          if (err.code === EmailPasswordError.ACCESS_DENIED) {
            setEmailPasswordError(EmailPasswordError.ACCESS_DENIED);
          } else {
            setEmailPasswordError(EmailPasswordError.GENERAL_ERROR);
            logError("Login error", err);
          }
        } else {
          // login should redirect to the auth0 and then back to the app
          console.debug("expected redirect, but nothing happened");
        }
      }
    );
  }, []);

  const logout = useCallback(() => {
    clearRemotePatientSession();
    clearTimerState();

    // Clear passwordless session data
    sessionStorage.removeItem("auth_method");
    sessionStorage.removeItem("pa_token");
    sessionStorage.removeItem("pa_dob");

    // Clear EHR session data
    sessionStorage.removeItem("ehr_connected");
    sessionStorage.removeItem("ehr_skipped");
    sessionStorage.removeItem("ehr_flow_complete");

    // End auth0 session. this doesn't affect app behavior, just here for completeness
    webAuth.logout();
  }, []);

  const clearPasswordlessState = useCallback(() => {
    clearTimerState();
    setEmailAddress("");
    setResendCodeRemainingSeconds(0);
  }, []);

  const refreshPasswordlessToken = useCallback(async () => {
    const authMethod = sessionStorage.getItem("auth_method");
    const paToken = sessionStorage.getItem("pa_token");
    const paDob = sessionStorage.getItem("pa_dob");
    const survey_kind = sessionStorage.getItem("kind");

    // Only refresh if using passwordless method and have cached credentials
    if (authMethod !== "passwordless" || !paToken || !paDob) {
      return false;
    }

    try {
      const response = await fetch(
        `${SPECIALIST_HOSTNAME}/api/v1/authentication/verify_magic_link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            pa: paToken,
            date_of_birth: paDob,
            survey_kind: survey_kind,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Update token and expiry
        const accessToken = data.access_token;
        localStorage.setItem("id_token", accessToken);
        const expiryTime = Date.now() + (data.expires_in || 300) * 1000;
        localStorage.setItem("id_token_exp", expiryTime.toString());

        // Decode JWT to extract patient identifiers from app_metadata
        try {
          const decoded = jwtDecode<CustomJwtPayload>(accessToken);
          const appMetadata = decoded.app_metadata;

          // Update stored identifiers from token
          if (appMetadata?.ciq_id) {
            localStorage.setItem("ciq_id", appMetadata.ciq_id);
          }
          if (appMetadata?.location_id) {
            localStorage.setItem("location_id", appMetadata.location_id);
          }
          if (decoded.email) {
            localStorage.setItem("ciq_email", decoded.email);
          }
        } catch (decodeError) {
          console.error("Error decoding refreshed token:", decodeError);
        }

        return true;
      }

      return false;
    } catch (err) {
      console.error("Token refresh failed:", err);
      return false;
    }
  }, []);

  const requestForgotPasswordReset = useCallback(
    async (email: string) => {
      setSubmitting(true);
      setEmailSent(false);

      const { surveyKind } = getLaunchState();
      const response = await requestPasswordReset(email, surveyKind);

      setSubmitting(false);
      setEmailSent(true);

      if (!response.ok) {
        setForgotPasswordError(true);
        logError("Error requesting password reset", response);
        return;
      }

      setForgotPasswordError(false);
      setEmailAddress(email);
      history.push("/login/forgot/confirm");
    },
    [history, setEmailAddress, setForgotPasswordError, setSubmitting]
  );

  const markMagicLinkAuthenticated = useCallback(() => {
    setAuthenticated(true);
    setLoading(false);
  }, []);

  const contextProps = useMemo(
    () => ({
      logout,
      isLoading,
      isEmailSent,
      isEmailResent,
      isSubmitting,
      emailAddress,
      setSubmitting,
      emailLinkError,
      verifyLoginCode,
      sendEmailWithCode,
      submitCredentials,
      isAuthenticated,
      passwordlessError,
      emailPasswordError,
      setEmailLinkError,
      setPasswordlessError,
      setEmailPasswordError,
      requestForgotPasswordReset,
      forgotPasswordError,
      setForgotPasswordError,
      resendCodeCount,
      setResendCodeCount,
      resendCodeRemainingSeconds,
      isResendCodeTimerActive,
      clearPasswordlessState,
      refreshPasswordlessToken,
      markMagicLinkAuthenticated,
    }),
    [
      logout,
      isLoading,
      isEmailSent,
      isEmailResent,
      emailAddress,
      isSubmitting,
      setSubmitting,
      emailLinkError,
      verifyLoginCode,
      submitCredentials,
      isAuthenticated,
      sendEmailWithCode,
      passwordlessError,
      emailPasswordError,
      requestForgotPasswordReset,
      forgotPasswordError,
      setForgotPasswordError,
      resendCodeCount,
      setResendCodeCount,
      resendCodeRemainingSeconds,
      isResendCodeTimerActive,
      clearPasswordlessState,
      refreshPasswordlessToken,
      markMagicLinkAuthenticated,
    ]
  );

  return (
    <PatientAuthContext.Provider value={contextProps}>
      {children}
    </PatientAuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const usePatientAuth = () => {
  const context = useContext(PatientAuthContext);

  if (context === undefined) {
    throw new Error("usePatientAuth must be used within a PatientAuthProvider");
  }
  return context;
};
